import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useToast } from "../../components/ui";
import { ApiError, api } from "../../lib/api";
import { readSheet } from "../../lib/sheet-read";

/** What Orbis keeps of a contact, and the column names that usually hold it (HubSpot's too). */
const FIELDS = [
  {
    key: "phone",
    label: "Teléfono o WhatsApp",
    required: true,
    guess: [
      "telefono",
      "celular",
      "movil",
      "whatsapp",
      "phone",
      "phonenumber",
      "mobilephonenumber",
      "mobilephone",
      "mobile",
      "numero",
      "tel",
    ],
  },
  {
    key: "firstName",
    label: "Nombre",
    guess: ["nombre", "name", "firstname", "nombrecompleto", "fullname", "contacto"],
  },
  { key: "lastName", label: "Apellido", guess: ["apellido", "apellidos", "lastname", "surname"] },
  { key: "email", label: "Correo", guess: ["correo", "email", "correoelectronico", "emailaddress", "mail"] },
  {
    key: "organization",
    label: "Empresa del contacto",
    guess: ["empresa", "organizacion", "compania", "company", "companyname", "associatedcompany", "negocio"],
  },
  {
    key: "organizationLegalId",
    label: "Cédula jurídica de la empresa",
    guess: ["cedulajuridica", "cedula", "legalid", "nit", "ruc"],
  },
  {
    key: "type",
    label: "Tipo (persona, empresa, institución)",
    guess: ["tipo", "tipodecontacto", "type", "contacttype"],
  },
  { key: "tags", label: "Etiquetas", guess: ["etiquetas", "etiqueta", "tags", "segmento", "segmentos"] },
  { key: "province", label: "Provincia", guess: ["provincia", "province", "state", "stateregion", "region"] },
  { key: "canton", label: "Cantón o ciudad", guess: ["canton", "ciudad", "city"] },
  { key: "notes", label: "Notas", guess: ["notas", "notes", "comentarios", "observaciones"] },
  {
    key: "externalId",
    label: "Id en el sistema anterior",
    guess: ["id", "recordid", "contactid", "idexterno", "externalid", "codigo"],
  },
] as const;
type FieldKey = (typeof FIELDS)[number]["key"];

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "");

function guessMapping(header: string[]) {
  const map: Partial<Record<FieldKey, number>> = {};
  const used = new Set<number>();
  for (const f of FIELDS) {
    const i = header.findIndex((h, idx) => !used.has(idx) && (f.guess as readonly string[]).includes(norm(h)));
    if (i >= 0) {
      map[f.key] = i;
      used.add(i);
    }
  }
  return map;
}

interface Result {
  created: number;
  updated: number;
  unchanged: number;
  organizationsCreated: number;
  skipped: number;
  problems: { row: number; reason: "phone" | "email" | "repeated" }[];
}
const REASON: Record<Result["problems"][number]["reason"], string> = {
  phone: "el teléfono no sirve para WhatsApp",
  email: "el correo no es válido",
  repeated: "el número ya venía en otra fila",
};
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
/** Rows per request: the API takes up to 1 000 at a time. */
const CHUNK = 1000;

/**
 * Contacts from another CRM's export (f6-07): pick the file (CSV or Excel), tell Orbis
 * which column is which, import. Importing again updates without duplicating.
 */
export function ContactImportDialog({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const [sheet, setSheet] = useState<{ header: string[]; rows: string[][] } | null>(null);
  const [map, setMap] = useState<Partial<Record<FieldKey, number>>>({});
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function onFile(f: File | undefined) {
    setError(null);
    setResult(null);
    if (!f) return setSheet(null);
    try {
      const [header = [], ...rows] = await readSheet(f);
      if (!rows.length) return setError("El archivo no tiene filas para importar.");
      setSheet({ header, rows });
      setMap(guessMapping(header));
    } catch {
      setError("No se pudo leer el archivo. Guárdalo como CSV o Excel (.xlsx) y vuelve a intentar.");
    }
  }

  const value = (r: string[], k: FieldKey) => (map[k] === undefined ? "" : (r[map[k] as number] ?? "").trim());
  const toItem = (r: string[], i: number) => ({
    row: i + 1,
    phone: value(r, "phone"),
    name: [value(r, "firstName"), value(r, "lastName")].filter(Boolean).join(" ").slice(0, 120) || null,
    email: value(r, "email") || null,
    organization: value(r, "organization").slice(0, 160) || null,
    organizationLegalId: value(r, "organizationLegalId").slice(0, 40) || null,
    type: value(r, "type").slice(0, 40) || null,
    tags: value(r, "tags").slice(0, 400) || null,
    province: value(r, "province").slice(0, 60) || null,
    canton: value(r, "canton").slice(0, 80) || null,
    notes: value(r, "notes").slice(0, 5000) || null,
    externalId: value(r, "externalId").slice(0, 120) || null,
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!sheet || map.phone === undefined) return;
    setError(null);
    const items = sheet.rows.map(toItem);
    const total: Result = { created: 0, updated: 0, unchanged: 0, organizationsCreated: 0, skipped: 0, problems: [] };
    setProgress({ done: 0, total: items.length });
    try {
      for (let i = 0; i < items.length; i += CHUNK) {
        const r = await api.post<Result>("/contacts/import", { items: items.slice(i, i + CHUNK) });
        total.created += r.created;
        total.updated += r.updated;
        total.unchanged += r.unchanged;
        total.organizationsCreated += r.organizationsCreated;
        total.skipped += r.skipped;
        total.problems.push(...r.problems);
        setProgress({ done: Math.min(i + CHUNK, items.length), total: items.length });
      }
      setResult(total);
      qc.invalidateQueries({ queryKey: ["contacts"] });
      toast(
        `${total.created} ${plural(total.created, "contacto nuevo", "contactos nuevos")}, ${total.updated} ${plural(total.updated, "actualizado", "actualizados")}`,
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? "Solo propietarios y administradores importan contactos."
          : "No se pudo terminar la importación. Lo que ya entró quedó guardado; puedes volver a importar el mismo archivo sin duplicar.",
      );
    } finally {
      setProgress(null);
    }
  }

  const sample = sheet?.rows.slice(0, 3).map(toItem) ?? [];
  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="cimp-title" className="contact-import">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="cimp-title">Importar contactos</h3>
        {result ? (
          <>
            <p>
              <strong>{result.created}</strong> {plural(result.created, "nuevo", "nuevos")},{" "}
              <strong>{result.updated}</strong> {plural(result.updated, "actualizado", "actualizados")}
              {result.unchanged
                ? `, ${result.unchanged} ${plural(result.unchanged, "ya estaba igual", "ya estaban igual")}`
                : ""}
              {result.organizationsCreated
                ? `, ${result.organizationsCreated} ${plural(result.organizationsCreated, "empresa nueva", "empresas nuevas")}`
                : ""}
              .
            </p>
            {result.skipped > 0 && (
              <>
                <p className="muted">
                  {result.skipped} {plural(result.skipped, "fila no entró", "filas no entraron")}:
                </p>
                <ul className="contact-import-problems">
                  {result.problems.map((p) => (
                    <li key={p.row}>
                      Fila {p.row + 1}: {REASON[p.reason]}
                    </li>
                  ))}
                  {result.skipped > result.problems.length && (
                    <li>…y {result.skipped - result.problems.length} más.</li>
                  )}
                </ul>
              </>
            )}
            <div className="row">
              <button className="btn btn-primary" type="button" onClick={() => dialog.current?.close()}>
                Listo
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="muted">
              Exporta los contactos de tu CRM anterior (HubSpot, Clientify, Excel…) como CSV o Excel. Cada contacto
              necesita su teléfono: Orbis lo usa para WhatsApp y para no duplicar. Si importas otra vez, solo se llenan
              los datos vacíos; lo que el equipo escribió en Orbis no se toca.
            </p>
            <div className="field">
              <label htmlFor="cimp-file">Archivo</label>
              <input
                className="input"
                id="cimp-file"
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => onFile(e.target.files?.[0])}
              />
            </div>
            {sheet && (
              <>
                <p className="muted">
                  {sheet.rows.length} filas. Elige qué columna del archivo va en cada dato; las que no uses quedan
                  vacías.
                </p>
                <div className="contact-import-map">
                  {FIELDS.map((f) => (
                    <label key={f.key} className="field">
                      <span>
                        {f.label}
                        {"required" in f && f.required ? " *" : ""}
                      </span>
                      <select
                        className="select"
                        value={map[f.key] ?? ""}
                        onChange={(e) =>
                          setMap({ ...map, [f.key]: e.target.value === "" ? undefined : Number(e.target.value) })
                        }
                      >
                        <option value="">No importar</option>
                        {sheet.header.map((h, i) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: a column is its position in the file
                          <option key={`${i}-${h}`} value={i}>
                            {h || `Columna ${i + 1}`}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
                {map.phone !== undefined && sample.length > 0 && (
                  <div className="contact-import-sample">
                    <p className="muted">Así se ven las primeras filas:</p>
                    <ul>
                      {sample.map((s) => (
                        <li key={s.row}>
                          <strong>{s.name ?? "Sin nombre"}</strong> · {s.phone || "sin teléfono"}
                          {s.email ? ` · ${s.email}` : ""}
                          {s.organization ? ` · ${s.organization}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            {sheet && map.phone === undefined && (
              <div className="banner banner-warn">Elige la columna del teléfono para poder importar.</div>
            )}
            {error && <div className="banner banner-warn">{error}</div>}
            <div className="row">
              <button className="btn" type="button" onClick={() => dialog.current?.close()} disabled={!!progress}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={!sheet || map.phone === undefined || !!progress}
              >
                {progress ? `Importando ${progress.done} de ${progress.total}…` : "Importar"}
              </button>
            </div>
          </>
        )}
      </form>
    </dialog>
  );
}
